import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import technicalMaintenanceApi from '../../api/technicalMaintenanceApi';
import DataTable from '../../components/ui/DataTable';
import IconActionButton from '../../components/ui/IconActionButton';
import TechnicalMaintenanceFormModal from './TechnicalMaintenanceFormModal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Select } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import {
  TechnicalMaintenancePageShell,
  formatDisplayDate,
  fmtMoney,
  statusBadgeClass,
} from './technicalMaintenanceUi';
import { suggestNextMaintenanceDate } from './technicalMaintenanceHelpers';

const initialForm = {
  equipment: '',
  maintenance_type: 'periodic',
  title: '',
  scheduled_date: '',
  completed_date: '',
  performed_by: '',
  vendor: '',
  description: '',
  replaced_parts: '',
  cost: '',
  status: 'planned',
  next_maintenance_date: '',
};

const TYPE_OPTIONS = [
  { value: 'periodic', label: 'Periyodik' },
  { value: 'preventive', label: 'Önleyici' },
  { value: 'emergency', label: 'Acil' },
  { value: 'revision', label: 'Revizyon' },
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planlandı' },
  { value: 'in_progress', label: 'Devam Ediyor' },
  { value: 'completed', label: 'Tamamlandı' },
  { value: 'cancelled', label: 'İptal' },
];

const defaultFilters = {
  search: '',
  equipment: '',
  maintenance_type: '',
  status: '',
  date_from: '',
  completed_date: '',
};

const normalizeIsoDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const getRecordSearchText = (row) => [
  row.title,
  row.description,
  row.replaced_parts,
  row.performed_by,
  row.vendor,
  row.equipment_name,
  row.equipment_area,
  row.maintenance_type_label,
  row.status_label,
].filter(Boolean).join(' ').toLowerCase();

const matchesMaintenanceFilters = (row, activeFilters) => {
  if (activeFilters.equipment && String(row.equipment) !== String(activeFilters.equipment)) return false;
  if (activeFilters.maintenance_type && row.maintenance_type !== activeFilters.maintenance_type) return false;
  if (activeFilters.status && row.status !== activeFilters.status) return false;
  if (activeFilters.date_from) {
    const scheduled = normalizeIsoDate(row.scheduled_date);
    if (!scheduled || scheduled < normalizeIsoDate(activeFilters.date_from)) return false;
  }
  if (activeFilters.completed_date
    && normalizeIsoDate(row.completed_date) !== normalizeIsoDate(activeFilters.completed_date)) {
    return false;
  }
  const searchTerm = activeFilters.search.trim().toLowerCase();
  if (searchTerm && !getRecordSearchText(row).includes(searchTerm)) return false;
  return true;
};

const TechnicalMaintenanceRecordsPage = () => {
  const { getFullName } = useAuth();
  const [searchParams] = useSearchParams();
  const equipmentFromUrl = searchParams.get('equipment') || '';
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filters, setFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [recordsResponse, equipmentResponse] = await Promise.all([
        technicalMaintenanceApi.getMaintenanceRecords(),
        technicalMaintenanceApi.getEquipment({ is_active: 'true' }),
      ]);
      setAllRows(recordsResponse?.data || []);
      setEquipment(equipmentResponse?.data || []);
    } catch (err) {
      setError(err.message || 'Bakım kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!equipmentFromUrl) return;
    const nextFilters = { ...defaultFilters, equipment: equipmentFromUrl };
    setDraftFilters(nextFilters);
    setFilters(nextFilters);
  }, [equipmentFromUrl]);

  useEffect(() => {
    setRows(allRows.filter((row) => matchesMaintenanceFilters(row, filters)));
  }, [allRows, filters]);

  const equipmentOptions = useMemo(
    () => equipment.map((item) => ({
      value: item.id,
      label: [item.name, item.area].filter(Boolean).join(' — '),
    })),
    [equipment]
  );

  const selectedEquipment = useMemo(
    () => equipment.find((item) => String(item.id) === String(formData.equipment)),
    [equipment, formData.equipment]
  );

  const applySuggestedNextDate = (nextForm, { force = false } = {}) => {
    if (!selectedEquipment?.maintenance_interval_days) return nextForm;
    if (!nextForm.completed_date) return nextForm;
    if (nextForm.status !== 'completed') return nextForm;
    if (nextForm.next_maintenance_date && !force) return nextForm;
    const suggested = suggestNextMaintenanceDate(
      nextForm.completed_date,
      selectedEquipment.maintenance_interval_days
    );
    if (!suggested) return nextForm;
    return { ...nextForm, next_maintenance_date: suggested };
  };

  const patchFormData = (patch, options) => {
    setFormData((prev) => applySuggestedNextDate({ ...prev, ...patch }, options));
  };

  const summary = useMemo(() => ({
    total: rows.length,
    completed: rows.filter((r) => r.status === 'completed').length,
    open: rows.filter((r) => !['completed', 'cancelled'].includes(r.status)).length,
  }), [rows]);

  const openCreate = () => {
    setEditingRow(null);
    setFormData({ ...initialForm, equipment: equipmentFromUrl || '' });
    setIsModalOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setFormData({
      equipment: row.equipment || '',
      maintenance_type: row.maintenance_type || 'periodic',
      title: row.title || '',
      scheduled_date: row.scheduled_date || '',
      completed_date: row.completed_date || '',
      performed_by: row.performed_by || '',
      vendor: row.vendor || '',
      description: row.description || '',
      replaced_parts: row.replaced_parts || '',
      cost: row.cost ?? '',
      status: row.status || 'planned',
      next_maintenance_date: row.next_maintenance_date || '',
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.equipment) {
      setError('Ekipman seçimi zorunludur.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...formData,
        equipment: Number(formData.equipment),
        cost: formData.cost === '' ? null : Number(formData.cost),
        scheduled_date: formData.scheduled_date || null,
        completed_date: formData.completed_date || null,
        next_maintenance_date: formData.next_maintenance_date || null,
      };
      if (editingRow) {
        await technicalMaintenanceApi.updateMaintenanceRecord(editingRow.id, payload);
      } else {
        await technicalMaintenanceApi.createMaintenanceRecord(payload);
      }
      setIsModalOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Bakım kaydı kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`"${row.title || row.equipment_name}" silinsin mi?`)) return;
    try {
      await technicalMaintenanceApi.deleteMaintenanceRecord(row.id);
      await loadData();
    } catch (err) {
      setError(err.message || 'Kayıt silinemedi.');
    }
  };

  const applyFilters = () => {
    setError(null);
    setFilters({ ...draftFilters });
  };

  const columns = useMemo(() => [
    {
      header: 'Ekipman',
      accessorKey: 'equipment_name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{row.original.equipment_name || '—'}</div>
          <div className="text-xs text-gray-500">{row.original.equipment_area || '—'}</div>
        </div>
      ),
    },
    {
      header: 'Durum',
      accessorKey: 'status',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span className={`tm-badge ${statusBadgeClass(row.original.status)} w-fit`}>
            {row.original.status_label}
          </span>
          <span className="text-xs text-gray-500">{row.original.maintenance_type_label}</span>
        </div>
      ),
    },
    {
      header: 'Tamamlanma',
      accessorKey: 'completed_date',
      cell: ({ getValue }) => formatDisplayDate(getValue()),
    },
    {
      header: 'Sonraki Bakım',
      accessorKey: 'next_maintenance_date',
      cell: ({ getValue }) => formatDisplayDate(getValue()),
    },
    {
      header: 'Yapılan İş',
      accessorKey: 'description',
      cell: ({ getValue }) => {
        const text = getValue();
        if (!text) return '—';
        return (
          <span className="block max-w-xs truncate text-sm text-gray-600 dark:text-gray-300" title={text}>
            {text}
          </span>
        );
      },
    },
    {
      header: 'Parçalar',
      accessorKey: 'replaced_parts',
      cell: ({ getValue }) => {
        const text = getValue();
        if (!text) return '—';
        return (
          <span className="block max-w-[10rem] truncate text-sm text-gray-500" title={text}>
            {text}
          </span>
        );
      },
    },
    {
      header: 'Maliyet',
      accessorKey: 'cost',
      cell: ({ getValue }) => fmtMoney(getValue()),
    },
    {
      header: '',
      accessorKey: 'actions',
      size: 80,
      cell: ({ row }) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <IconActionButton icon={PencilSquareIcon} title="Düzenle" onClick={() => openEdit(row.original)} />
          <IconActionButton icon={TrashIcon} title="Sil" variant="danger" onClick={() => handleDelete(row.original)} />
        </div>
      ),
    },
  ], []);

  return (
    <TechnicalMaintenancePageShell
      title="Bakım Kayıtları"
      subtitle="Tamir, servis ve periyodik bakım geçmişini ekipman bazında yönetin."
      actions={(
        <Button onClick={openCreate}>
          <PlusIcon className="h-4 w-4 mr-1 inline" />
          Yeni Kayıt
        </Button>
      )}
    >
      <div className="tm-summary-bar">
        <span className="tm-summary-pill">Toplam <strong>{summary.total}</strong></span>
        <span className="tm-summary-pill">Tamamlanan <strong>{summary.completed}</strong></span>
        <span className="tm-summary-pill">Açık <strong>{summary.open}</strong></span>
      </div>

      <div className="tm-inline-filters">
        <div className="field wide">
          <Label>Hızlı arama</Label>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              value={draftFilters.search}
              onChange={(e) => setDraftFilters({ ...draftFilters, search: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              placeholder="Ekipman, parça, firma, açıklama..."
            />
          </div>
        </div>
        <Button variant="secondary" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Filtreleri Gizle' : 'Detaylı Filtre'}
        </Button>
        <Button onClick={applyFilters}>Ara</Button>
      </div>

      {showFilters && (
        <div className="tm-inline-filters">
          <div className="field wide">
            <Label>Ekipman</Label>
            <SearchableSelect
              options={equipmentOptions}
              value={draftFilters.equipment}
              onChange={(nextValue) => setDraftFilters({ ...draftFilters, equipment: nextValue })}
              placeholder="Ekipman seç..."
            />
          </div>
          <div className="field">
            <Label>Tip</Label>
            <Select
              value={draftFilters.maintenance_type}
              onChange={(e) => setDraftFilters({ ...draftFilters, maintenance_type: e.target.value })}
            >
              <option value="">Tümü</option>
              {TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div className="field">
            <Label>Durum</Label>
            <Select
              value={draftFilters.status}
              onChange={(e) => setDraftFilters({ ...draftFilters, status: e.target.value })}
            >
              <option value="">Tümü</option>
              {STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
          </div>
          <div className="field">
            <Label>Plan (başlangıç)</Label>
            <Input
              type="date"
              value={draftFilters.date_from}
              onChange={(e) => setDraftFilters({ ...draftFilters, date_from: e.target.value })}
            />
          </div>
          <div className="field">
            <Label>Tamamlanma</Label>
            <Input
              type="date"
              value={draftFilters.completed_date}
              onChange={(e) => setDraftFilters({ ...draftFilters, completed_date: e.target.value })}
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              setDraftFilters(defaultFilters);
              setFilters(defaultFilters);
            }}
          >
            Temizle
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      <div className="tm-dash-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataTable
          columns={columns}
          data={rows}
          isLoading={loading}
          showSearch={false}
          onRowClick={openEdit}
        />
      </div>

      <TechnicalMaintenanceFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        wide
        title={editingRow ? 'Bakım Kaydı Düzenle' : 'Yeni Bakım Kaydı'}
        footer={(
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
          </>
        )}
      >
        <div className="space-y-5">
          <section className="tm-form-section">
            <h4 className="tm-form-section-title">Ekipman & Durum</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Ekipman</Label>
                <SearchableSelect
                  options={equipmentOptions}
                  value={formData.equipment}
                  onChange={(nextValue) => patchFormData({ equipment: nextValue })}
                  placeholder="Makine seçin..."
                />
              </div>
              <div>
                <Label>Başlık</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Kısa özet (opsiyonel)" />
              </div>
              <div>
                <Label>Bakım Tipi</Label>
                <Select value={formData.maintenance_type} onChange={(e) => setFormData({ ...formData, maintenance_type: e.target.value })}>
                  {TYPE_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Durum</Label>
                <Select value={formData.status} onChange={(e) => patchFormData({ status: e.target.value })}>
                  {STATUS_OPTIONS.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </Select>
              </div>
            </div>
          </section>

          <section className="tm-form-section">
            <h4 className="tm-form-section-title">Tarihler & Sorumlular</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Plan Tarihi</Label>
                <Input type="date" value={formData.scheduled_date} onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} />
              </div>
              <div>
                <Label>Tamamlanma</Label>
                <Input type="date" value={formData.completed_date} onChange={(e) => patchFormData({ completed_date: e.target.value })} />
              </div>
              <div>
                <Label>Sonraki Bakım</Label>
                <Input type="date" value={formData.next_maintenance_date} onChange={(e) => setFormData({ ...formData, next_maintenance_date: e.target.value })} />
                {selectedEquipment?.maintenance_interval_days ? (
                  <p className="text-xs text-gray-500 mt-1">
                    Ekipman aralığı: {selectedEquipment.maintenance_interval_days} gün
                    {formData.completed_date && formData.status === 'completed' ? (
                      <>
                        {' · '}
                        <button
                          type="button"
                          className="text-highlight-600 hover:underline"
                          onClick={() => patchFormData({}, { force: true })}
                        >
                          Otomatik hesapla
                        </button>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <div>
                <Label>Bakımı Yapan</Label>
                <Input value={formData.performed_by} onChange={(e) => setFormData({ ...formData, performed_by: e.target.value })} />
              </div>
              <div>
                <Label>Firma</Label>
                <Input value={formData.vendor} onChange={(e) => setFormData({ ...formData, vendor: e.target.value })} />
              </div>
              <div>
                <Label>Kaydı Açan</Label>
                <Input
                  value={editingRow?.created_by_name || getFullName() || '—'}
                  readOnly
                  disabled
                  className="bg-gray-50 dark:bg-gray-800/60 cursor-not-allowed"
                />
              </div>
            </div>
          </section>

          <section className="tm-form-section">
            <h4 className="tm-form-section-title">İş Detayı</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Yapılan İş</Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Ne yapıldı? Arıza neydi?"
                  className="flex min-h-[100px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight-500/30 focus-visible:border-highlight-500 resize-y"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Değişen Parçalar</Label>
                <textarea
                  value={formData.replaced_parts}
                  onChange={(e) => setFormData({ ...formData, replaced_parts: e.target.value })}
                  rows={3}
                  placeholder="Conta, motor, filtre..."
                  className="flex min-h-[80px] w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight-500/30 focus-visible:border-highlight-500 resize-y"
                />
              </div>
              <div>
                <Label>Maliyet (TRY)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  placeholder="0,00"
                />
              </div>
            </div>
          </section>
        </div>
      </TechnicalMaintenanceFormModal>
    </TechnicalMaintenancePageShell>
  );
};

export default TechnicalMaintenanceRecordsPage;
